/* eslint-disable import/no-duplicates */
import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from '../../../../store/store.types';

import { deskEditRoles, DeskGroupFilters, DeskGroupModel, DeskGroupRequest } from '../../../../api/core/core.types';
import { DeskGroupActionTypes } from '../../types/deskgroup.types';
import { addNewDeskGroup, deleteDeskGroup, editDeskGroup, getDeskGroupList } from '../../actions/deskgroup.actions';
import DeskGroupModal from '../../../../components/modals/DeskGroupModal';
import { defaultPageSize, PageRequest } from '../../../../api/api.types';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { WithTranslation, withTranslation } from 'react-i18next';
import ListViewTable from '../../../../components/tables/ListViewTable';

interface DeskGroupsProps extends RouteComponentProps, WithTranslation {
  getDeskGroupList: (filters?: DeskGroupFilters, page?: PageRequest) => void;
  addDeskGroup: (request: DeskGroupModel) => void;
  editDeskGroup: (id: number, request: DeskGroupModel) => void;
  deleteDeskGroup: (id: number) => void;
  deskGroups: DeskGroupModel[] | null;
  totalElements: number;
  totalPages: number;
  user: UserModel | null;
}

interface DeskGroupsState {
  isModalOpen: boolean;
  selectedDeskGroup: DeskGroupModel | null;
  selectedPageNumber: number;
  sortOrder: 'ASC' | 'DESC';
  sortedBy: string;
  isDeleteModalOpen: boolean;
  ratePlanToBeDeleted?: number;
  filter: {
    nameFilter: {
      value: string;
    };
  };
}

const DeskGroupSortFields = [
  { value: 'name', label: 'NAME' }
];

class DeskGroups extends React.Component<DeskGroupsProps, DeskGroupsState> {
  constructor(props: DeskGroupsProps) {
    super(props);
    this.state = {
      isModalOpen: false,
      selectedDeskGroup: null,
      selectedPageNumber: 1,
      sortOrder: 'ASC',
      sortedBy: 'name',
      isDeleteModalOpen: false,
      ratePlanToBeDeleted: undefined,
      filter: {
        nameFilter: {
          value: ''
        }
      }
    };
  }

  componentDidMount() {
    this.props.getDeskGroupList();
  }

  onAdd = () => {
    this.setState({ isModalOpen: true, selectedDeskGroup: null });
  };

  onConfirm = (nameValue: string, descriptionValue?: string) => {
    if (!this.state.selectedDeskGroup) {
      this.props.addDeskGroup({ name: nameValue, description: descriptionValue } as DeskGroupModel);

    } else if (this.state.selectedDeskGroup?.id) {
      this.props.editDeskGroup(this.state.selectedDeskGroup.id, {
        name: nameValue,
        description: descriptionValue
      } as DeskGroupModel);
    }

    this.setState({ isModalOpen: false, selectedDeskGroup: null });
  };

  onDelete = (id: number) => {
    this.setState({ ratePlanToBeDeleted: id, isModalOpen: false, isDeleteModalOpen: true });
  };

  onDeleteConfirm = () => {
    if (this.state.ratePlanToBeDeleted) {
      this.props.deleteDeskGroup(this.state.ratePlanToBeDeleted);
      this.setState({ isDeleteModalOpen: false, ratePlanToBeDeleted: undefined });
    }
  };

  onFilterClick = (e: FormEvent) => {
    e.preventDefault();
    this.props.getDeskGroupList(this.composeDeskGroupFilters());
  };

  clearFilters = () => {
    this.setState(
      {
        filter: {
          nameFilter: {
            value: ''
          }
        }
      }
    );

    this.props.getDeskGroupList();
  };

  changeHandler = (event: { target: HTMLInputElement }) => {
    const { name, value } = event.target;
    this.setState({
      filter: {
        ...this.state.filter,
        [ name ]: {
          value
        }
      }
    });
  };

  onRowClick = (rowInfo: any) => {
    if (this.props.deskGroups) {
      const deskGroup = this.props.deskGroups.find((dg) => dg.id === rowInfo.original.id);
      if (deskGroup) {
        this.setState({ isModalOpen: true, selectedDeskGroup: deskGroup });
      }
    }
  };

  sortBy = (columnName: string, sortDirection: 'ASC' | 'DESC') => {
    const sortFieldName = DeskGroupSortFields.find((column) => column.value === columnName);
    if (!sortFieldName) {
      return;
    }
    this.setState({ sortOrder: sortDirection, sortedBy: columnName });
    return (
      this.props.getDeskGroupList(this.composeDeskGroupFilters(), {
        page: this.state.selectedPageNumber - 1,
        size: defaultPageSize,
        sortField: sortFieldName.label,
        sortDirection: sortDirection
      })
    );
  };

  getDataFn(pageRequest: PageRequest) {
    if (pageRequest.page !== undefined && pageRequest.sortField !== undefined && pageRequest.sortDirection !== undefined) {
      this.props.getDeskGroupList(this.composeDeskGroupFilters(), pageRequest);

      this.setState({
        selectedPageNumber: pageRequest.page + 1,
        sortOrder: pageRequest.sortDirection,
        sortedBy: pageRequest.sortField
      });
    }
  }

  composeDeskGroupFilters = () => ({ name: this.state.filter.nameFilter.value });

  defineColumns = () => {
    const { t } = this.props;

    return [
      {
        header: t('ct.desk_groups.name'), accessorKey: 'name'
      },
      {
        header: t('ct.desk_groups.desks'), accessorKey: 'deskCount'
      },
      {
        header: t('ct.desk_group.description'), accessorKey: 'description'
      }
    ];
  };

  render() {
    const { t } = this.props;

    return (
      <main>
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-center">
            <h1>{t('page_title.desk_groups')}</h1>
            {this.props.user && userCanAccessComponent(this.props.user, deskEditRoles) && (
              <button type="submit" className="btn btn-primary btn-wide" onClick={this.onAdd}>
                {t('b.add_new').toUpperCase()}</button>
            )}
          </div>
          <form className="d-flex justify-content-between container-fluid filter-container form-group form-inline">
            <div className="d-flex row ml-1 filters-container-margin-top-btm">
              <input
                value={this.state.filter.nameFilter.value}
                type="text"
                name="nameFilter"
                className="form-control mr-1"
                placeholder={t('ph.desk_groups.filter.by_name')}
                onChange={this.changeHandler}
              />
            </div>
            <div>
              <button type="button" className="btn btn-text-only mr-2" onClick={() => this.clearFilters()}>
                {t('b.clear_filter')}
              </button>
              <button type="submit" className="btn btn-primary filter-button" onClick={this.onFilterClick}>
                {t('b.filter').toUpperCase()}
              </button>
            </div>
          </form>
          {this.props.deskGroups ? (
            <div className="table-list">
              <ListViewTable
                data={this.props.deskGroups}
                columns={this.defineColumns()}
                getTrProps={this.onRowClick}
                sortBy={this.sortBy}
                sortOrder={this.state.sortOrder}
                sortedBy={this.state.sortedBy}
                paginationEnabled={true}
                totalPages={this.props.totalPages}
                totalElements={this.props.totalElements}
                selectedPageNumber={this.state.selectedPageNumber}
                getDataFn={(pageRequest: PageRequest) => this.getDataFn(pageRequest)}
              />
            </div>
          ) : null}
        </div>
        <DeskGroupModal
          isOpen={this.state.isModalOpen}
          selectedDeskGroup={this.state.selectedDeskGroup}
          toggle={() => this.setState({ isModalOpen: false })}
          onConfirm={(name, description) => this.onConfirm(name, description)}
          onCancel={() => this.setState({ isModalOpen: false })}
          onDelete={(id: number) => this.onDelete(id)}
        />
        <ConfirmationModal
          title="Confirm desk group removal"
          content="This will also remove all rate plans related to this desk group"
          isOpen={this.state.isDeleteModalOpen}
          toggle={() => this.setState({ isDeleteModalOpen: false })}
          onConfirm={() => this.onDeleteConfirm()}
          onCancel={() => this.setState({ isDeleteModalOpen: false })}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  deskGroups: state.setup.deskGroups.deskGroups,
  totalElements: state.setup.deskGroups.totalElements,
  totalPages: state.setup.deskGroups.totalPages,
  user: state.authentication.user
});

const mapDispatchToProps = (dispatch: ThunkDispatch<{}, {}, DeskGroupActionTypes>) => ({
  getDeskGroupList: (filters?: DeskGroupFilters, page?: PageRequest) => dispatch(getDeskGroupList(filters, page)),
  deleteDeskGroup: (id: number) => dispatch(deleteDeskGroup(id)),
  editDeskGroup: (id: number, request: DeskGroupRequest) => dispatch(editDeskGroup(id, request)),
  addDeskGroup: (request: DeskGroupRequest) => dispatch(addNewDeskGroup(request))
});

export default withTranslation()(connect(
  mapStateToProps,
  mapDispatchToProps
)(DeskGroups));
